import { useState } from "react";
import styled from "styled-components";

import { useQuest, useQuestAction, useStartQuest } from "../api/hooks";
import type { QuestActionResult } from "../api/types";
import { getUser } from "../auth/session";
import { Button, Card, Chip, ErrorNotice, Loading, SectionLabel } from "../components/primitives";

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space(4)};
`;

const Thread = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space(3)};
`;

const Intro = styled.p`
  margin: 0 0 ${({ theme }) => theme.space(4)};
  line-height: 1.5;
  color: ${({ theme }) => theme.color.text};
`;

// A chat-style "dungeon master" message from Rocky.
const Msg = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px;
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.color.surface};
  border: 1px solid ${({ theme }) => theme.color.border};
`;

const DMAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.color.accent};
  color: #fff;
  font-weight: 700;
  font-size: 13px;
`;

const MsgBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const MsgLabel = styled.div`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 10px;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.color.textFaint};
  margin-bottom: 4px;
`;

const MsgText = styled.div`
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.color.text};
`;

// The player's submitted action, indented to align under the DM avatar.
const PlayerMsg = styled.div`
  margin-left: 32px;
  padding: 16px;
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.color.surfaceAlt};
  border: 1px solid ${({ theme }) => theme.color.border};
`;

const RollBox = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: ${({ theme }) => theme.radius.lg};
  border: 1px solid rgba(0, 152, 234, 0.25);
  background: linear-gradient(135deg, rgba(0, 152, 234, 0.12), transparent);
`;

const DieWrap = styled.div`
  width: 58px;
  height: 58px;
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DieValue = styled.span`
  position: relative;
  z-index: 1;
  font-family: ${({ theme }) => theme.font.ui};
  font-weight: 700;
  font-size: 22px;
  line-height: 1;
  color: #fff;
`;

const RollInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const RollLabel = styled.div`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 10px;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.color.textFaint};
`;

const RollValue = styled.div`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 22px;
  font-weight: 700;
  line-height: 1.1;
  color: ${({ theme }) => theme.color.accent};
`;

const RollVerdict = styled.div<{ $ok: boolean }>`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 11px;
  margin-top: 2px;
  color: ${({ theme, $ok }) => ($ok ? theme.color.satiety : theme.color.danger)};
`;

const Rewards = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space(2)};
  flex-wrap: wrap;
  margin-top: 10px;
`;

// Input dock styled like Telegram's composer.
const Dock = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 8px 8px 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.color.surface};
  border: 1px solid ${({ theme }) => theme.color.borderHi};

  &:focus-within {
    border-color: ${({ theme }) => theme.color.accent};
  }
`;

const DockPrefix = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 13px;
  color: ${({ theme }) => theme.color.accent};
`;

const DockInput = styled.input`
  flex: 1;
  min-width: 0;
  background: transparent;
  border: none;
  outline: none;
  color: ${({ theme }) => theme.color.text};
  font-size: 14px;
`;

const SendBtn = styled.button`
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border-radius: 10px;
  border: none;
  background: ${({ theme }) => theme.color.accent};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
`;

function critOf(r: QuestActionResult): "success" | "failure" | "none" {
  if (r.criticalSuccess) return "success";
  if (r.criticalFailure) return "failure";
  return "none";
}

function D20Die({ value }: { value: number }) {
  return (
    <DieWrap>
      <svg width="58" height="58" viewBox="0 0 58 58" style={{ position: "absolute" }}>
        <polygon
          points="29,4 53,18 53,42 29,56 5,42 5,18"
          fill="rgba(0,152,234,0.12)"
          stroke="#0098EA"
          strokeWidth="1.5"
        />
        <polygon
          points="29,4 53,18 29,30 5,18"
          fill="rgba(0,152,234,0.08)"
          stroke="rgba(0,152,234,0.3)"
          strokeWidth="0.8"
        />
        <polygon
          points="5,18 29,30 29,56"
          fill="rgba(0,0,0,0.2)"
          stroke="rgba(0,152,234,0.3)"
          strokeWidth="0.8"
        />
        <polygon
          points="53,18 29,30 29,56"
          fill="rgba(0,0,0,0.35)"
          stroke="rgba(0,152,234,0.3)"
          strokeWidth="0.8"
        />
      </svg>
      <DieValue>{value}</DieValue>
    </DieWrap>
  );
}

export function Quest() {
  const { data, isLoading, error } = useQuest();
  const start = useStartQuest();
  const act = useQuestAction();
  const [action, setAction] = useState("");

  if (isLoading) return <Loading label="loading quest…" />;
  if (error) return <ErrorNotice message={error.message} />;

  const active = data?.active || start.data?.active;
  const scenario = start.data?.scenario ?? data?.scenario;
  const result = act.data;
  // The action that produced `result`, not the live input value.
  const submittedAction = act.variables?.action;
  const rateLimited = result?.rateLimited;
  const resolved = result?.resolved;

  const user = getUser();
  const youLabel = `@${user?.username ?? user?.firstName ?? "you"} · /action`;

  function submit() {
    const a = action.trim();
    if (!a || act.isPending) return;
    act.mutate({ action: a });
  }

  return (
    <Stack>
      <Header>
        <SectionLabel>// quest</SectionLabel>
      </Header>

      {!active ? (
        <Card ticks>
          <Intro>No quest is running. Roll the dice on a fresh adventure with Rocky.</Intro>
          {start.error && <ErrorNotice message={start.error.message} />}
          <Button $full disabled={start.isPending} onClick={() => start.mutate()}>
            {start.isPending ? "summoning…" : "Start a quest"}
          </Button>
        </Card>
      ) : (
        <Thread>
          {scenario && (
            <Msg>
              <DMAvatar>DM</DMAvatar>
              <MsgBody>
                <MsgLabel>ROCKY · dungeon master</MsgLabel>
                <MsgText>{scenario}</MsgText>
              </MsgBody>
            </Msg>
          )}

          {result && rateLimited && (
            <>
              <PlayerMsg>
                <MsgLabel>{youLabel}</MsgLabel>
                <MsgText>{submittedAction || "…"}</MsgText>
              </PlayerMsg>
              <Msg>
                <DMAvatar>DM</DMAvatar>
                <MsgBody>
                  <MsgLabel>ROCKY</MsgLabel>
                  <MsgText>
                    That&rsquo;s your daily share of adventuring, pal. Rocky waves a tiny paw
                    goodbye — come back tomorrow.
                  </MsgText>
                </MsgBody>
              </Msg>
            </>
          )}

          {result && !rateLimited && (
            <>
              <PlayerMsg>
                <MsgLabel>{youLabel}</MsgLabel>
                <MsgText>{submittedAction || "…"}</MsgText>
              </PlayerMsg>
              <RollBox>
                <D20Die value={result.d20} />
                <RollInfo>
                  <RollLabel>D20 ROLL</RollLabel>
                  <RollValue>{result.d20}</RollValue>
                  <RollVerdict $ok={result.success}>
                    →{" "}
                    {critOf(result) === "success"
                      ? "critical success"
                      : critOf(result) === "failure"
                        ? "critical failure"
                        : result.success
                          ? "success"
                          : "failure"}
                  </RollVerdict>
                </RollInfo>
              </RollBox>
              <Msg>
                <DMAvatar>DM</DMAvatar>
                <MsgBody>
                  <MsgLabel>ROCKY</MsgLabel>
                  <MsgText>{result.outcome}</MsgText>
                  {(result.pointsAwarded != null ||
                    (result.petHappinessDelta != null && result.petHappinessDelta !== 0)) && (
                    <Rewards>
                      {result.pointsAwarded != null && (
                        <Chip $tone="accent">+{result.pointsAwarded} quest pts</Chip>
                      )}
                      {result.petHappinessDelta != null && result.petHappinessDelta !== 0 && (
                        <Chip $tone={result.petHappinessDelta > 0 ? "good" : "bad"}>
                          {result.petHappinessDelta > 0 ? "+" : ""}
                          {result.petHappinessDelta} happiness
                        </Chip>
                      )}
                    </Rewards>
                  )}
                </MsgBody>
              </Msg>
            </>
          )}
        </Thread>
      )}

      {active && !resolved && (
        <div>
          {act.error && <ErrorNotice message={act.error.message} />}
          <Dock>
            <DockPrefix>/action</DockPrefix>
            <DockInput
              value={action}
              placeholder="what do you do?"
              onChange={(e) => setAction(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
            />
            <SendBtn disabled={!action.trim() || act.isPending} onClick={submit} aria-label="send">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path
                  d="M1 7H13M13 7L8 2M13 7L8 12"
                  stroke="#fff"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </SendBtn>
          </Dock>
        </div>
      )}

      {resolved && (
        <Button
          $full
          $variant="ghost"
          disabled={start.isPending}
          onClick={() => {
            // Clear the resolved action result so the new quest starts clean.
            act.reset();
            setAction("");
            start.mutate();
          }}
        >
          {start.isPending ? "summoning…" : "Start another quest"}
        </Button>
      )}
    </Stack>
  );
}
